import React, { useEffect, useState } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import Experience from './components/Experience';
import Projects from './components/Projects';
import Contact from './components/Contact';
import ParticleOrbBackground from './components/ParticleOrbBackground';
import Dock from './components/ui/Dock';
import ScrollProgressBar from './components/ui/ScrollProgressBar';
import ClickSpark from './components/ui/ClickSpark';
import { Home, User, Briefcase, FolderOpen, Mail } from 'lucide-react';
import './App.css';

const SECTION_IDS = ['home', 'about', 'experience', 'projects', 'contact'] as const;

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
  window.scrollTo({ top: y, behavior: 'smooth' });
};

/** Tracks which section currently dominates the viewport. */
function useActiveSection(): string {
  const [active, setActive] = useState<string>('home');

  useEffect(() => {
    let ticking = false;
    const update = () => {
      const probe = window.scrollY + window.innerHeight * 0.35;
      let current: string = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= probe) current = id;
      }
      setActive(current);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return active;
}

function App() {
  const activeSection = useActiveSection();

  const dockItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Home', isActive: activeSection === 'home', onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
    { icon: <User className="w-5 h-5" />, label: 'About', isActive: activeSection === 'about', onClick: () => scrollTo('about') },
    { icon: <Briefcase className="w-5 h-5" />, label: 'Experience', isActive: activeSection === 'experience', onClick: () => scrollTo('experience') },
    { icon: <FolderOpen className="w-5 h-5" />, label: 'Projects', isActive: activeSection === 'projects', onClick: () => scrollTo('projects') },
    { icon: <Mail className="w-5 h-5" />, label: 'Contact', isActive: activeSection === 'contact', onClick: () => scrollTo('contact') },
  ];

  return (
    <ClickSpark sparkColor="#667eea" sparkCount={8} sparkSize={5}>
      <div className="App">
        <ParticleOrbBackground />
        <ScrollProgressBar />

        <div className="relative z-10 px-4 md:px-6 lg:px-8">
          <Hero />
          <About />
          <Experience />
          <Projects />
          <Contact />
        </div>

        {/* Floating Dock nav */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <Dock items={dockItems} panelHeight={64} baseItemSize={44} magnification={66} />
        </div>
      </div>
    </ClickSpark>
  );
}

export default App;
